/**
 * ClassCarousel.jsx — The Sessions: Two featured class cards
 *
 * Two side-by-side cards with a playful opposite tilt. Each card shows the
 * class illustration, an "At the Studio" label, a Titan One title, a Raleway
 * subtitle, and a Hero-style CTA button.
 *
 * Props:
 *   onExplore {Function} — called when "See Our Sessions" button is clicked
 *   onGallery {Function} — kept for compatibility
 */
import { motion } from 'framer-motion';
import { CLASSES } from '../data/classes';
import { useStudioTilesContent } from '../services/firestore';

const SESSIONS = CLASSES.filter((c) => c.carousel);

// Map class id → public illustration path
const ILLUSTRATIONS = {
  1: '/Handbuilt1.png',
  2: '/WHEEL LARGE.png',
};

// Map class id → tile key in Firestore
const TILE_KEY = { 1: 'handBuilding', 2: 'wheelThrowing' };

export default function ClassCarousel({ onGallery }) {
  const { tiles } = useStudioTilesContent();
  return (
    <div className="section-pad" id="classes-section">
      {/* ── Two cards — float directly on the page background ─────────────── */}
      <div
        style={{
          display: 'flex',
          gap: 32,
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        {SESSIONS.map((c, i) => {
          const isFirst = i === 0;
          const tileData = tiles[TILE_KEY[c.id]] || {};
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 48, rotate: isFirst ? -4 : 4 }}
              whileInView={{ opacity: 1, y: 0, rotate: isFirst ? -2 : 2 }}
              viewport={{ once: true, amount: 0.15 }}
              animate={{
                y: [0, -4, 0],
                scale: [1, 1.02, 1],
              }}
              style={{
                flex: '1 1 300px',
                maxWidth: 420,
                background: '#F9F2E9',
                borderRadius: 20,
                overflow: 'hidden',
                boxShadow: '0 8px 40px rgba(26, 18, 8, 0.11)',
                position: 'relative',
                zIndex: 51,
                cursor: 'default',
              }}
              transition={{
                opacity: {
                  duration: 0.7,
                  ease: [0.22, 1, 0.36, 1],
                  delay: i * 0.12,
                },
                rotate: {
                  duration: 0.7,
                  ease: [0.22, 1, 0.36, 1],
                  delay: i * 0.12,
                },
                y: {
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.2,
                },
                scale: {
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.2,
                },
              }}
              whileHover={{
                rotate: 0,
                y: -6,
                boxShadow: '0 16px 56px rgba(26, 18, 8, 0.16)',
                transition: { duration: 0.25 },
              }}
            >
              {/* ── Illustration ── */}
              <div
                style={{
                  width: '100%',
                  height: 340,
                  overflow: 'hidden',
                  background: '#EDE6DA',
                }}
              >
                <img
                  src={tileData.illustrationUrl || ILLUSTRATIONS[c.id]}
                  alt={c.carouselTitle || c.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: isFirst ? 'center top' : 'right top',
                    display: 'block',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>

              {/* ── Card content ── */}
              <div style={{ padding: '24px 28px 32px', textAlign: 'center' }}>
                {/* "At the Studio" label — replaces pill */}
                <p
                  style={{
                    fontFamily: "'Raleway', sans-serif",
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#9C7A3C',
                    letterSpacing: '0.01em',
                    marginBottom: 10,
                  }}
                >
                  {tileData.carouselLabel || 'At the Studio'}
                </p>

                {/* Title */}
                <h3
                  style={{
                    fontFamily: "'Titan One', sans-serif",
                    fontWeight: 400,
                    fontSize: 'clamp(1.4rem, 2.5vw, 1.8rem)',
                    color: '#1a1208',
                    letterSpacing: '-0.02em',
                    textTransform: 'uppercase',
                    marginBottom: 10,
                    lineHeight: 1.1,
                  }}
                >
                  {tileData.carouselTitle || c.carouselTitle || c.title}
                </h3>

                {/* Subtitle */}
                <p
                  style={{
                    fontFamily: "'Raleway', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(0.9rem, 1.4vw, 1rem)',
                    color: '#9C7A3C',
                    lineHeight: 1.6,
                    marginBottom: 28,
                  }}
                >
                  {tileData.carouselSub || c.carouselSub || c.sub}
                </p>

                {/* CTA — cobalt blue for first card, amber for second */}
                {isFirst ? (
                  <button
                    onClick={() => onGallery(c)}
                    style={{
                      width: '100%',
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '1rem',
                      background: '#4A6FA5',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 50,
                      padding: '16px 36px',
                      cursor: 'pointer',
                      letterSpacing: '0.01em',
                      transition:
                        'background 0.2s ease, transform 0.18s ease, box-shadow 0.18s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#3A5A8F';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow =
                        '0 8px 24px rgba(74, 111, 165, 0.38)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#4A6FA5';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {tileData.buttonText || 'View Gallery'}
                  </button>
                ) : (
                  <button
                    onClick={() => onGallery(c)}
                    style={{
                      width: '100%',
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '1rem',
                      background: '#E8A020',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 50,
                      padding: '16px 36px',
                      cursor: 'pointer',
                      letterSpacing: '0.01em',
                      transition:
                        'background 0.2s ease, transform 0.18s ease, box-shadow 0.18s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#d4911a';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow =
                        '0 8px 24px rgba(232, 160, 32, 0.38)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#E8A020';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {tileData.buttonText || 'View Gallery'}
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
